// PAN validator. 100% local. Validates structure + checksum hint.
//
// Format: AAAAA9999A. First five chars are alphabetic, next four numeric,
// final char alphabetic check letter. The 4th character encodes status
// (P = individual, H = HUF, F = firm, C = company, T = trust, etc).
// The Income Tax Department does not publish the check-letter algorithm,
// so the validator stops at structural integrity + the public 4th-char
// taxonomy. That is enough to reject 99% of typos.

export const PAN_HOLDER_TYPE: Record<string, { en: string; hi: string }> = {
  P: { en: 'Individual', hi: 'व्यक्तिगत' },
  H: { en: 'HUF', hi: 'हिंदू अविभाजित परिवार' },
  F: { en: 'Firm / LLP', hi: 'फर्म / एलएलपी' },
  C: { en: 'Company', hi: 'कंपनी' },
  A: { en: 'Association of Persons', hi: 'व्यक्तियों का संघ' },
  T: { en: 'Trust', hi: 'न्यास' },
  B: { en: 'Body of Individuals', hi: 'व्यक्तियों का निकाय' },
  L: { en: 'Local Authority', hi: 'स्थानीय निकाय' },
  J: { en: 'Artificial Juridical Person', hi: 'कृत्रिम न्यायिक व्यक्ति' },
  G: { en: 'Government', hi: 'सरकार' },
};

export type PanHolderCode = keyof typeof PAN_HOLDER_TYPE;

export interface PanInfo {
  readonly raw: string;
  readonly normalised: string;
  readonly holderCode: PanHolderCode;
  readonly holderLabelEn: string;
  readonly holderLabelHi: string;
  readonly surnameInitial: string;
  readonly status: 'valid' | 'structural' | 'invalid';
}

const PAN_STRUCTURE = /^[A-Z]{5}\d{4}[A-Z]$/;
const KNOWN_HOLDER_CODES = new Set(Object.keys(PAN_HOLDER_TYPE));

export function normalisePan(input: string): string {
  return input.toUpperCase().replace(/\s+/g, '');
}

export function validatePan(input: string): PanInfo {
  const normalised = normalisePan(input);
  if (!PAN_STRUCTURE.test(normalised)) {
    return {
      raw: input,
      normalised,
      holderCode: 'P',
      holderLabelEn: '',
      holderLabelHi: '',
      surnameInitial: '',
      status: 'invalid',
    };
  }
  const code4 = normalised[3] ?? 'P';
  const isKnown = KNOWN_HOLDER_CODES.has(code4);
  const display = isKnown ? PAN_HOLDER_TYPE[code4] : undefined;
  return {
    raw: input,
    normalised,
    holderCode: (isKnown ? code4 : 'P') as PanHolderCode,
    holderLabelEn: display?.en ?? 'Unrecognised holder code',
    holderLabelHi: display?.hi ?? 'अज्ञात कोड',
    surnameInitial: normalised[4] ?? '',
    status: isKnown ? 'valid' : 'structural',
  };
}

export function maskPan(pan: string): string {
  const n = normalisePan(pan);
  if (n.length !== 10) return '**********';
  return `${n.slice(0, 3)}****${n.slice(7)}`;
}
